import React, { useEffect, useState, useMemo } from 'react';
import { useStore } from "../../../../../hooks";
import { observer } from "mobx-react";
import { UCPHistoryGroups } from "../../../../../utils/enums";
import dayjs from 'dayjs';
import {
    HistoryContainer,
    ConversationGroup,
    GroupTitle,
    ConversationCard,
    ConversationTitle,
    ConversationDate,
    EmptyMessage,
    LoadingContainer
} from './HistoryTab.styled';
import { CircularProgress } from '@mui/material';
import useSearchNavigation from '../hooks/useSearchNavigation';

const HistoryTab = observer(({ onTabChange }) => {
    const {
        historyAgentStore,
        assistanceUCPStore,
        alertStore,
    } = useStore();

    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const selectedConversationId = historyAgentStore.selectedConversationId;

    const contactId = alertStore.segmentId;

    const searchConfig = {
        searchTerm: assistanceUCPStore.searchTerm,
        highlightedResult: assistanceUCPStore.highlightedResult,
        setSearchTerm: (term) => assistanceUCPStore.setSearchTerm(term),
        setHighlightedResult: (index) => assistanceUCPStore.setHighlightedResult(index),
        setFilteredConversations: (conversations) => assistanceUCPStore.setFilteredConversations(conversations)
    };

    const { highlightMatchedText } = useSearchNavigation(
        assistanceUCPStore.filteredConversations,
        searchConfig
    );

    const filteredConversations = useMemo(() => {
        if (!assistanceUCPStore.filteredConversations.length) return [];
        if (!assistanceUCPStore.searchTerm) return assistanceUCPStore.filteredConversations;

        return assistanceUCPStore.filteredConversations.filter(conversation =>
            conversation.title.toLowerCase().includes(assistanceUCPStore.searchTerm.toLowerCase())
        );
    }, [assistanceUCPStore.filteredConversations, assistanceUCPStore.searchTerm]);

    useEffect(() => {
        const loadConversations = async () => {
            if (assistanceUCPStore.conciergeAgent) {
                setIsLoadingConversations(true);
                try {
                    await historyAgentStore.fetchConversations(
                        assistanceUCPStore.conciergeAgent.id,
                        assistanceUCPStore.customer
                    );
                } finally {
                    setIsLoadingConversations(false);
                }
            }
        };

        loadConversations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistanceUCPStore.conciergeAgent, assistanceUCPStore.customer]);

    useEffect(() => {
        const conversationId = assistanceUCPStore.conversationID;
        if (conversationId && assistanceUCPStore.conversationHistory.length > 0) {
            if (conversationId === historyAgentStore.selectedConversationId) {
                const existingConversationIndex = historyAgentStore.conversations.findIndex(
                    conv => conv.id === conversationId
                );
                
                if (existingConversationIndex >= 0) {
                    const updatedConversation = {
                        ...historyAgentStore.conversations[existingConversationIndex],
                        last_modified: new Date().toISOString()
                    };
                    historyAgentStore.addConversation(updatedConversation);
                    return;
                }
            }
            
            const newConversation = {
                id: conversationId,
                title: assistanceUCPStore.conversationHistory[0].message,
                last_modified: new Date().toISOString(),
                metadata: {
                    ref_id: contactId
                }
            };
            historyAgentStore.addConversation(newConversation);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistanceUCPStore.conversationID, assistanceUCPStore.conversationHistory, contactId]);

    const handleConversationClick = async (conversation) => {
        assistanceUCPStore.cleanupConversation();
        await historyAgentStore.fetchMessages(conversation.id);
        historyAgentStore.setLastConversationID(conversation.id);
        historyAgentStore.setSelectedConversationId(conversation.id);

        if (historyAgentStore.messagesData.length > 0) {
            assistanceUCPStore.setConversationHistory(historyAgentStore.messagesData);
            assistanceUCPStore.setConversationID(conversation.id);
            assistanceUCPStore.setSelectedTab('Chat');
            if (onTabChange && typeof onTabChange === 'function') {
                onTabChange(0);
            }
        }
    };

    const formatDate = (date) => dayjs(date).format('MMM DD, YYYY');
    const getMonthYearKey = (date) => dayjs(date).format('MMMM - YYYY').toLowerCase();

    const getGroupedConversations = () => {
        const now = dayjs();
        const today = now.startOf('day');
        const yesterday = today.subtract(1, 'day');
        const lastWeek = today.subtract(7, 'days');
        const lastMonth = today.subtract(30, 'days');

        const grouped = {
            [UCPHistoryGroups.TODAY]: [],
            [UCPHistoryGroups.YESTERDAY]: [],
            [UCPHistoryGroups.LAST_WEEK]: [],
            [UCPHistoryGroups.LAST_MONTH]: []
        };

        const olderConversations = {};

        filteredConversations.forEach(conversation => {
            const conversationDate = dayjs(conversation.last_modified);

            if (conversationDate.isAfter(today) || conversationDate.isSame(today, 'day')) {
                grouped[UCPHistoryGroups.TODAY].push(conversation);
            } else if (conversationDate.isAfter(yesterday) || conversationDate.isSame(yesterday, 'day')) {
                grouped[UCPHistoryGroups.YESTERDAY].push(conversation);
            } else if (conversationDate.isAfter(lastWeek)) {
                grouped[UCPHistoryGroups.LAST_WEEK].push(conversation);
            } else if (conversationDate.isAfter(lastMonth)) {
                grouped[UCPHistoryGroups.LAST_MONTH].push(conversation);
            } else {
                const monthYearKey = getMonthYearKey(conversationDate);
                if (!olderConversations[monthYearKey]) {
                    olderConversations[monthYearKey] = [];
                }
                olderConversations[monthYearKey].push(conversation);
            }
        });

        const sortedOlderKeys = Object.keys(olderConversations).sort((a, b) => {
            const dateA = dayjs(a, 'MMMM - YYYY');
            const dateB = dayjs(b, 'MMMM - YYYY');
            return dateB.diff(dateA);
        });

        sortedOlderKeys.forEach(key => {
            grouped[key] = olderConversations[key];
        });

        return grouped;
    };

    const getFlatFilteredList = () => {
        if (!assistanceUCPStore.searchTerm) return assistanceUCPStore.filteredConversations;
        
        return assistanceUCPStore.filteredConversations.filter(conversation => 
            conversation.title.toLowerCase().includes(assistanceUCPStore.searchTerm.toLowerCase()));
    };

    const renderGroups = () => {
        const groupedConversations = getGroupedConversations();
        const flatFilteredList = getFlatFilteredList();

        return Object.entries(groupedConversations).map(([group, groupConversations]) => {
            if (groupConversations.length === 0) return null;

            return (
                <ConversationGroup key={group}>
                    <GroupTitle>{group}</GroupTitle>
                    {groupConversations.map((conversation) => {
                        const matchesSearch = assistanceUCPStore.searchTerm &&
                            conversation.title.toLowerCase().includes(assistanceUCPStore.searchTerm.toLowerCase());
                        
                        const flatIndex = matchesSearch ?
                            flatFilteredList.findIndex(c => c.id === conversation.id) : -1;
                        
                        const isHighlighted = matchesSearch &&
                            assistanceUCPStore.highlightedResult >= 0 && 
                            flatIndex === assistanceUCPStore.highlightedResult;

                        const highlightedTitle = matchesSearch && assistanceUCPStore.searchTerm
                            ? highlightMatchedText(conversation.title, assistanceUCPStore.searchTerm)
                            : conversation.title;

                        return (
                            <ConversationCard
                                key={conversation.id}
                                id={`conversation-${conversation.id}`}
                                data-index={flatIndex}
                                className={`searchable-card ${selectedConversationId === conversation.id ? 'active' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                                onClick={() => handleConversationClick(conversation)}
                            >
                                <ConversationTitle 
                                    dangerouslySetInnerHTML={{ __html: highlightedTitle }}
                                />
                                <ConversationDate>
                                    {formatDate(conversation.last_modified)}
                                </ConversationDate>
                            </ConversationCard>
                        );
                    })}
                </ConversationGroup>
            );
        });
    };

    useEffect(() => {
        if (assistanceUCPStore.searchTerm && assistanceUCPStore.highlightedResult >= 0) {
            const flatFilteredList = getFlatFilteredList();
            if (flatFilteredList.length > 0 && assistanceUCPStore.highlightedResult < flatFilteredList.length) {
                const targetConversation = flatFilteredList[assistanceUCPStore.highlightedResult];
                if (targetConversation) {
                    document.querySelectorAll('.searchable-card').forEach(card => {
                        card.classList.remove('highlighted');
                    });
                    
                    const targetCard = document.getElementById(`conversation-${targetConversation.id}`);
                    if (targetCard) {
                        targetCard.classList.add('highlighted');
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assistanceUCPStore.highlightedResult, assistanceUCPStore.searchTerm]);

    if (isLoadingConversations) {
        return (
            <LoadingContainer>
                <CircularProgress size={24} />
            </LoadingContainer>
        );
    }

    if (!filteredConversations?.length) {
        return (
            <EmptyMessage>
                {assistanceUCPStore.searchTerm ? 'No conversations found' : 'There are no conversations available'}
            </EmptyMessage>
        );
    }

    return (
        <HistoryContainer className="history-container">
            {renderGroups()}
        </HistoryContainer>
    );
});

export default HistoryTab;